import { selectAuthState } from 'app/store/selectors'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isAllowlistedAdminEmail } from './adminConfig'

/**
 * Centralized admin-access check.
 *
 * Returns `true` when there is a currently authenticated user who is either:
 *   1. assigned the `admin` role on their `TuhiUserDto` (`role === 'admin'`,
 *      the ACA-102 org-admin role model), OR
 *   2. on the configured email allowlist (see `adminConfig.ts`) — retained as
 *      a bootstrap fallback/override so the first admins can reach the console
 *      before any role has been assigned.
 *
 * Every admin gate in the app — route guards, nav items, screens — MUST consume
 * this hook rather than reading the role or allowlist directly, so the backing
 * source can be swapped in one place.
 *
 * Secure-by-default: returns `false` when the user is unauthenticated, is not
 * an admin by role, and is not on the allowlist.
 *
 * ACA-102: `role` is the primary signal now; the email allowlist stays as a
 * bootstrap override until every org has at least one role-assigned admin.
 */
export const useIsAdmin = (): boolean => {
    const { isAuthenticated, currentUser } = useSelector(selectAuthState)

    return useMemo(() => {
        if (!isAuthenticated || !currentUser) {
            return false
        }

        return (
            currentUser.role === 'admin' ||
            isAllowlistedAdminEmail(currentUser.email)
        )
    }, [isAuthenticated, currentUser])
}
